import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronDown, Info, AlertCircle, CheckCircle2 } from "lucide-react"
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useCartContext } from '../contexts/CartContext';
import { sendOrderConfirmationEmail } from '../lib/emailService';
import TransferInstructionsModal from '../components/TransferInstructionsModal';
import { ProxyImage } from '../lib/imageProxy';

// Tipo para errores de campos individuales
interface FieldErrors {
  email?: string;
  contactPerson?: string;
  nit?: string;
  address?: string;
  department?: string;
  municipality?: string;
  postalCode?: string;
  phone?: string;
  selectedBank?: string;
  bankTransferReceipt?: string;
}

// Interfaces para departamentos y municipios
interface Departamento {
  id: string;
  nombre: string;
  created_at?: string;
}

interface Municipio {
  id: string;
  nombre: string;
  departamento: string;
  created_at?: string;
}

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  price_type?: 'contado' | 'credito' | 'mayorista' | 'publico';
  product: {
    name: string;
    base_price: number;
    prices?: {
      price_type?: 'contado' | 'credito' | 'mayorista' | 'publico';
      min_quantity: number;
      price: number;
      offer_price?: number;
    }[];
    main_image: string;
  };
}

interface FormData {
  email: string;
  companyName: string;
  contactPerson: string;
  nit: string;
  nitType: 'NIT' | 'CF';
  address: string;
  addressDetails: string;
  department: string;
  municipality: string;
  postalCode: string;
  phone: string;
  cardNumber?: string;
  cardExpiry?: string;
  cardCvc?: string;
  cardHolder?: string;
  bankTransferReceipt?: File;
  selectedBank?: string;
}

export default function Checkout() {
  const { id: orderId } = useParams();
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [country, setCountry] = useState("United States")
  const [address, setAddress] = useState("")
  const [cardNumber, setCardNumber] = useState("")
  const [expiry, setExpiry] = useState("")
  const [cvc, setCvc] = useState("")
  const [sameAsShipping, setSameAsShipping] = useState(true)

  // Add these new state variables
  const [deliveryOption, setDeliveryOption] = useState("home")
  const [usesSavedAddress, setUsesSavedAddress] = useState(false)
  const [company, setCompany] = useState("")
  const [contactPerson, setContactPerson] = useState("")
  const [taxType, setTaxType] = useState("NIT")
  const [taxNumber, setTaxNumber] = useState("")
  const [addressDetails, setAddressDetails] = useState("")
  const [department, setDepartment] = useState("Guatemala")
  const [municipality, setMunicipality] = useState("Guatemala")
  const [postalCode, setPostalCode] = useState("")
  const [phone, setPhone] = useState("")
  const [saveInfo, setSaveInfo] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'bankTransfer'>('card')
  const [selectedBank, setSelectedBank] = useState("")

  // Estado para modo orden existente
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  const [orderPaymentStatus, setOrderPaymentStatus] = useState<string | null>(null);
  const [orderAlreadyPaid, setOrderAlreadyPaid] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Estados de lógica migrada
  const navigate = useNavigate();
  const { refreshCart } = useCartContext();
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const formRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loadingCart, setLoadingCart] = useState(true);
  const [userPriceType, setUserPriceType] = useState<'contado' | 'credito' | 'mayorista' | 'publico' | null>(null);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState<Municipio[]>([]);
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    companyName: '',
    contactPerson: '',
    nit: '',
    nitType: 'NIT',
    address: '',
    addressDetails: '',
    department: '',
    municipality: '',
    postalCode: '',
    phone: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardHolder: '',
    bankTransferReceipt: undefined,
    selectedBank: '',
  });

  // Efectos de carga inicial
  useEffect(() => {
    if (orderId) {
      // Modo pago de orden existente
      fetchOrderById(orderId);
      fetchDepartamentos();
    } else {
      fetchUserProfile();
      fetchDepartamentos();
      fetchCartItems();
      fetchUserPriceType();
    }
  }, [orderId]);

  // --- FUNCIONES MIGRADAS ---
  // Cargar tipo de precio del usuario
  const fetchUserPriceType = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('price_type')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      setUserPriceType(profile?.price_type || null);
    } catch (err) {
      console.error('Error fetching user price type:', err);
    }
  };

  // Cargar productos del carrito
  const fetchCartItems = async () => {
    try {
      setLoadingCart(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      const { data: cart, error: cartError } = await supabase
        .from('cart')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (cartError) throw cartError;
      if (!cart) throw new Error('No se encontró el carrito');
      const { data: items, error: itemsError } = await supabase
        .from('cart_items')
        .select(`
          id,
          quantity,
          product_id,
          price_type,
          product:products(
            name,
            base_price,
            prices:product_prices(
              price_type,
              min_quantity,
              price,
              offer_price
            ),
            main_image
          )
        `)
        .eq('cart_id', cart.id);
      if (itemsError) throw itemsError;
      if (!items || items.length === 0) {
        navigate('/order-summary');
        return;
      }
      const transformedItems: CartItem[] = items.map(item => {
        if (!item.product || typeof item.product !== 'object') {
          return {
            id: item.id,
            product_id: item.product_id,
            quantity: item.quantity,
            price_type: item.price_type,
            product: {
              name: 'Producto no disponible',
              base_price: 0,
              prices: [],
              main_image: ''
            }
          };
        }
        return {
          id: item.id,
          product_id: item.product_id,
          quantity: item.quantity,
          price_type: item.price_type,
          product: {
            name: (item.product as any).name,
            base_price: (item.product as any).base_price,
            prices: (item.product as any).prices,
            main_image: (item.product as any).main_image
          }
        };
      });
      setCartItems(transformedItems);
    } catch (error) {
      setError('Error al cargar los productos del carrito');
    } finally {
      setLoadingCart(false);
    }
  };

  // Cargar perfil de usuario
  const fetchUserProfile = async () => {
    try {
      setLoadingProfile(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (error) return;
      if (profile) {
        setUserProfile(profile);
        setFormData(prev => {
          const updatedData = {
            ...prev,
            email: profile.email || user.email || '',
            companyName: profile.company || '',
            contactPerson: profile.name || '',
            phone: profile.phone || '',
            nit: profile.nit || '',
            nitType: profile.nit_type || 'NIT',
            address: profile.address || '',
            addressDetails: profile.address_details || '',
            department: profile.department || '',
            municipality: profile.municipio || '',
            postalCode: profile.postal_code || ''
          };
          if (profile.department) {
            fetchMunicipiosByDepartamento(profile.department, true); // true = mantener municipio actual
          }
          return updatedData;
        });
      }
    } catch (error) {
      //
    } finally {
      setLoadingProfile(false);
    }
  };

  // Cargar departamentos
  const fetchDepartamentos = async () => {
    try {
      setLoadingDepartamentos(true);
      const { data, error } = await supabase.rpc('get_departamentos');
      if (error) return;
      const departamentosFormateados = data.map((nombre: string, index: number) => ({
        id: index.toString(),
        nombre: nombre
      }));
      setDepartamentos(departamentosFormateados);
    } catch (error) {
      //
    } finally {
      setLoadingDepartamentos(false);
    }
  };

  // Cargar municipios por departamento
  const fetchMunicipiosByDepartamento = async (departamentoNombre: string, keepCurrentMunicipality = false) => {
    try {
      setLoadingMunicipios(true);
      setMunicipiosFiltrados([]); // Limpiar municipios anteriores

      // Solo limpiar el municipio si no estamos cargando datos iniciales del perfil
      if (!keepCurrentMunicipality) {
        setFormData(prev => ({ ...prev, municipality: '' }));
      }

      if (!departamentoNombre) {
        setLoadingMunicipios(false);
        return;
      }

      const { data, error } = await supabase.rpc('get_municipios', {
        p_departamento: departamentoNombre,
      });

      if (error) {
        console.error('Error fetching municipios:', error);
        setLoadingMunicipios(false);
        return;
      }

      // La función devuelve un array de strings, convertir a objetos Municipio
      const municipiosFormateados: Municipio[] = (data || []).map((nombre: string, index: number) => ({
        id: index.toString(),
        nombre: nombre,
        departamento: departamentoNombre
      }));

      setMunicipiosFiltrados(municipiosFormateados);
    } catch (error) {
      console.error('Error fetching municipios:', error);
    } finally {
      setLoadingMunicipios(false);
    }
  };

  // Cargar datos de una orden existente
  const fetchOrderById = async (id: string) => {
    try {
      setLoadingCart(true);
      setLoadingProfile(true);
      const { data: order, error } = await supabase
        .from('orders')
        .select(`*, items:order_items(*, product:products(*))`)
        .eq('id', id)
        .single();
      if (error || !order) {
        setError('No se pudo cargar la orden.');
        setLoadingCart(false);
        setLoadingProfile(false);
        return;
      }
      // Estado de la orden
      setOrderStatus(order.status);
      setOrderPaymentStatus(order.payment_status);
      setOrderAlreadyPaid(order.payment_status === 'paid');
      // Cargar productos de la orden
      const items: CartItem[] = (order.items || []).map((item: any) => ({
        id: item.id,
        product_id: item.product_id,
        quantity: item.quantity,
        price_type: item.price_type,
        product: {
          name: item.product?.name || 'Producto no disponible',
          base_price: item.price,
          prices: item.product?.prices || [],
          main_image: item.product?.main_image || ''
        }
      }));
      setCartItems(items);
      // Cargar dirección y datos de usuario
      if (order.shipping_address) {
        let shipping = order.shipping_address;
        if (typeof shipping === 'string') {
          try { shipping = JSON.parse(shipping); } catch { shipping = {}; }
        }
        setFormData(prev => ({
          ...prev,
          email: order.email || shipping.email || '',
          companyName: shipping.company || '',
          contactPerson: shipping.first_name || '',
          phone: shipping.phone || '',
          nit: order.nit || shipping.nit || '',
          nitType: order.nit_type || shipping.nit_type || 'NIT',
          address: shipping.address || '',
          addressDetails: shipping.address_details || '',
          department: shipping.region || '',
          municipality: shipping.city || '',
          postalCode: shipping.postal_code || ''
        }));
      }
      setLoadingCart(false);
      setLoadingProfile(false);
    } catch (err) {
      setError('No se pudo cargar la orden.');
      setLoadingCart(false);
      setLoadingProfile(false);
    }
  };

  // Manejar cambios en el formulario - limpia error del campo al escribir
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (fieldErrors[name as keyof FieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
    // Limpiar error general también
    if (error) setError(null);
  };

  // Función para manejar la carga del comprobante de transferencia
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Verificar que sea una imagen
      if (!file.type.startsWith('image/')) {
        setError('Por favor, sube una imagen como comprobante');
        return;
      }
      // Verificar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen no debe superar los 5MB');
        return;
      }
      setFormData(prev => ({
        ...prev,
        bankTransferReceipt: file
      }));
      setError(null);
    }
  };

  // Validar formulario - valida todos los campos y muestra errores inline
  const validateForm = () => {
    const errors: FieldErrors = {};
    let firstErrorField: string | null = null;

    // Validar email
    if (!formData.email) {
      errors.email = 'Ingresa tu correo electrónico';
      if (!firstErrorField) firstErrorField = 'email';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'El correo no es válido (ej: nombre@email.com)';
        if (!firstErrorField) firstErrorField = 'email';
      }
    }

    // Validar nombre (empresa o persona)
    if (!formData.companyName && !formData.contactPerson) {
      errors.contactPerson = 'Ingresa tu nombre o el de tu empresa';
      if (!firstErrorField) firstErrorField = 'contactPerson';
    }

    // Validar NIT (solo si no es CF)
    if (formData.nitType === 'NIT') {
      if (!formData.nit) {
        errors.nit = 'Ingresa tu NIT';
        if (!firstErrorField) firstErrorField = 'nit';
      } else {
        const nitRegex = /^\d{5,9}(-?\d)?$/;
        if (!nitRegex.test(formData.nit.replace(/\s/g, ''))) {
          errors.nit = 'NIT no válido (ej: 12345678-9)';
          if (!firstErrorField) firstErrorField = 'nit';
        }
      }
    }

    // Validar dirección
    if (!formData.address) {
      errors.address = 'Ingresa tu dirección';
      if (!firstErrorField) firstErrorField = 'address';
    } else if (formData.address.length < 5) {
      errors.address = 'La dirección es muy corta';
      if (!firstErrorField) firstErrorField = 'address';
    }

    // Validar departamento
    if (!formData.department) {
      errors.department = 'Selecciona un departamento';
      if (!firstErrorField) firstErrorField = 'department';
    }

    // Validar municipio
    if (!formData.municipality) {
      errors.municipality = 'Selecciona un municipio';
      if (!firstErrorField) firstErrorField = 'municipality';
    }

    // Validar teléfono (más flexible)
    if (!formData.phone) {
      errors.phone = 'Ingresa tu número de teléfono';
      if (!firstErrorField) firstErrorField = 'phone';
    } else {
      const cleanPhone = formData.phone.replace(/\s|-/g, '');
      if (cleanPhone.length < 8) {
        errors.phone = 'El teléfono debe tener 8 dígitos';
        if (!firstErrorField) firstErrorField = 'phone';
      }
    }

    // Validaciones para transferencia bancaria
    if (paymentMethod === 'bankTransfer') {
      if (!formData.selectedBank) {
        errors.selectedBank = 'Selecciona el banco al que transferiste';
        if (!firstErrorField) firstErrorField = 'selectedBank';
      }
      if (!formData.bankTransferReceipt) {
        errors.bankTransferReceipt = 'Sube la foto del comprobante';
        if (!firstErrorField) firstErrorField = 'bankTransferReceipt';
      }
    }

    // Actualizar errores de campo
    setFieldErrors(errors);

    // Si hay errores, mostrar mensaje general y hacer scroll
    if (Object.keys(errors).length > 0) {
      const errorCount = Object.keys(errors).length;
      setError(`Por favor completa ${errorCount === 1 ? 'el campo marcado' : `los ${errorCount} campos marcados`} en rojo`);

      // Scroll al primer error
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (element as HTMLInputElement).focus();
        }
      }

      // Scroll al mensaje de error
      setTimeout(() => {
        errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      return false;
    }

    return true;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // VALIDACIÓN DE PRECIOS EN BACKEND - Previene manipulación
      const cartItemsForValidation = cartItems.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        claimed_price: getProductPrice(item)
      }));

      const { data: validationResult, error: validationError } = await supabase
        .rpc('validate_and_calculate_order_total', {
          p_user_id: user.id,
          p_cart_items: cartItemsForValidation
        });

      if (validationError) {
        console.error('Error validando precios:', validationError);
        setError('Error al validar los precios. Por favor, recarga la página e intenta nuevamente.');
        setLoading(false);
        return;
      }

      // Verificar si la validación fue exitosa
      const validation = validationResult?.[0];
      if (!validation?.is_valid) {
        setError(validation?.error_message || 'Los precios han cambiado. Por favor, recarga la página.');
        setLoading(false);
        return;
      }

      // Usar el total calculado por el servidor + costo de envío
      const shippingCost = getShippingCost();
      const subtotal = validation.calculated_total;
      const total = subtotal + shippingCost;

      // Preparar la dirección de envío
      const shippingAddress = {
        first_name: formData.contactPerson,
        last_name: '',
        company: formData.companyName,
        nit: formData.nitType === 'CF' ? '' : formData.nit,
        nit_type: formData.nitType,
        address: formData.address,
        address_details: formData.addressDetails,
        city: formData.municipality,
        region: formData.department,
        postal_code: formData.postalCode,
        phone: formData.phone,
        email: formData.email
      };
      if (paymentMethod === 'card') {
        // --- PAGO CON TARJETA (Recurrente) ---
        // NO creamos el pedido aquí. Solo guardamos una sesión de checkout.
        // El pedido real se crea en el webhook cuando Recurrente confirma el pago.
        // El carrito NO se vacía hasta que el pago sea confirmado.

        // Usar los precios validados del servidor para el snapshot del carrito
        const validatedItems = validation.validated_items || [];
        const cartSnapshot = validatedItems.map((item: { product_id: string; quantity: number; validated_price: number }) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.validated_price
        }));

        // Crear sesión de checkout (NO es un pedido todavía)
        const { data: checkoutSession, error: sessionError } = await supabase
          .from('checkout_sessions')
          .insert([{
            user_id: user.id,
            cart_snapshot: cartSnapshot,
            shipping_address: shippingAddress,
            total_amount: total,
            shipping_cost: shippingCost,
            payment_method: 'card',
            delivery_method: 'delivery',
            nit: formData.nitType === 'CF' ? '' : formData.nit,
            nit_type: formData.nitType,
            email: formData.email,
            status: 'pending'
          }])
          .select('id')
          .single();

        if (sessionError) throw sessionError;

        // Preparar items para Recurrente (amount_in_cents)
        const recurrenteItems = validatedItems.map((item: { product_id: string; quantity: number; validated_price: number }) => {
          const cartItem = cartItems.find(ci => ci.product_id === item.product_id);
          return {
            name: cartItem?.product?.name || 'Producto',
            amount_in_cents: Math.round(item.validated_price * 100),
            currency: 'GTQ',
            quantity: item.quantity,
          };
        });

        type RecurrenteResponse = {
          id?: string;
          checkout_url?: string;
          error?: string;
        };

        // Crear checkout en Recurrente
        const { data: linkData, error: linkError } = await supabase.functions.invoke('recurrente-create-checkout', {
          body: {
            checkoutSessionId: checkoutSession.id,
            items: recurrenteItems,
            metadata: {
              userId: user.id,
              checkoutSessionId: checkoutSession.id,
              isCheckoutSession: true
            }
          }
        });

        const recurrenteResponse = linkData as RecurrenteResponse | null;

        if (linkError || !recurrenteResponse?.checkout_url) {
          // Si falla, eliminar la sesión creada
          await supabase.from('checkout_sessions').delete().eq('id', checkoutSession.id);

          const apiMessage = linkError?.message || recurrenteResponse?.error;
          setError(apiMessage ? `Recurrente: ${apiMessage}` : 'No se pudo generar el enlace de pago. Intenta nuevamente más tarde.');
          setLoading(false);
          return;
        }

        // Actualizar la sesión con la info del checkout de Recurrente
        await supabase
          .from('checkout_sessions')
          .update({
            payment_link_id: recurrenteResponse.id || null,
            payment_link_url: recurrenteResponse.checkout_url,
            updated_at: new Date().toISOString()
          })
          .eq('id', checkoutSession.id);

        if (saveInfo) await saveProfileInfo();

        setSuccess('Redirigiendo a Recurrente para completar tu pago...');
        window.location.href = recurrenteResponse.checkout_url;
        return;
      } else if (paymentMethod === 'bankTransfer') {
        // --- PAGO POR TRANSFERENCIA ---
        if (formData.bankTransferReceipt) {
          const fileName = `receipt_${Date.now()}.${formData.bankTransferReceipt.name.split('.').pop()}`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('public')
            .upload(`receipts/${fileName}`, formData.bankTransferReceipt, {
              cacheControl: '3600',
              upsert: true
            });
          if (uploadError) {
            setError(`Error al subir el comprobante: ${uploadError.message}`);
            setLoading(false);
            return;
          }
          const { data: publicUrlData } = supabase.storage
            .from('public')
            .getPublicUrl(`receipts/${fileName}`);
          const receiptUrl = publicUrlData?.publicUrl;
          // Crear el pedido
          const { data: newOrder, error: orderError } = await supabase
            .from('orders')
            .insert([{
              user_id: user.id,
              total_amount: total,
              shipping_cost: shippingCost,
              status: 'pending_payment',
              payment_status: 'pending',
              payment_method: paymentMethod,
              delivery_method: 'delivery',
              shipping_address: JSON.stringify(shippingAddress),
              nit: formData.nitType === 'CF' ? '' : formData.nit,
              nit_type: formData.nitType,
              transfer_receipt_url: receiptUrl,
              email: user.email
            }])
            .select('id')
            .single();
          if (orderError) throw orderError;
          // Insertar los items del pedido usando precios validados del servidor
          const validatedItemsTransfer = validation.validated_items || [];
          const orderItems = validatedItemsTransfer.map((item: { product_id: string; quantity: number; validated_price: number }) => ({
            order_id: newOrder.id,
            product_id: item.product_id,
            quantity: item.quantity,
            price: item.validated_price
          }));
          const { error: orderItemsError } = await supabase
            .from('order_items')
            .insert(orderItems);
          if (orderItemsError) throw orderItemsError;
          // Limpiar el carrito
          await clearCart();
          // Guardar información en el perfil si está marcado el checkbox
          if (saveInfo) await saveProfileInfo();

          // Encolar emails de notificación
          const orderUrl = `https://disolgua.com/order/${newOrder.id}`;
          const adminOrderUrl = `https://disolgua.com/admin/orders/${newOrder.id}`;
          const customerName = formData.contactPerson || formData.companyName || 'Cliente';

          // Preparar items para el email con nombre del producto
          const emailItems = cartItems.map(item => {
            const itemPrice = getProductPrice(item);
            return {
              name: item.product?.name || 'Producto',
              quantity: item.quantity,
              price: itemPrice,
              subtotal: item.quantity * itemPrice,
              image: item.product?.main_image
            };
          });

          // Email al cliente: comprobante recibido, verificando pago
          await supabase.from('email_queue').insert({
            type: 'transfer_pending_verification',
            recipient: formData.email,
            subject: `Recibimos tu comprobante - Pedido #${newOrder.id}`,
            data: {
              orderId: newOrder.id,
              customerName,
              totalAmount: total,
              shippingCost: shippingCost,
              subtotal: subtotal,
              items: emailItems,
              shippingAddress: `${formData.address}, ${formData.city}, ${formData.department}`,
              orderDate: new Date().toLocaleDateString('es-GT', { year: 'numeric', month: 'long', day: 'numeric' }),
              orderUrl
            },
            status: 'pending'
          });

          // Emails a admins configurados
          const adminEmailData = {
            orderId: newOrder.id,
            customerName,
            customerEmail: formData.email,
            customerPhone: formData.phone,
            totalAmount: total,
            shippingCost: shippingCost,
            subtotal: subtotal,
            paymentMethod: 'bankTransfer',
            items: emailItems,
            shippingAddress: `${formData.address}, ${formData.city}, ${formData.department}`,
            orderDate: new Date().toLocaleDateString('es-GT', { year: 'numeric', month: 'long', day: 'numeric' }),
            orderUrl: adminOrderUrl
          };

          // Obtener emails de admin para nuevo pedido
          const { data: newOrderSettings } = await supabase
            .from('notification_settings')
            .select('emails, enabled')
            .eq('notification_type', 'new_order')
            .single();

          if (newOrderSettings?.enabled && newOrderSettings.emails?.length > 0) {
            for (const adminEmail of newOrderSettings.emails) {
              await supabase.from('email_queue').insert({
                type: 'admin_new_order',
                recipient: adminEmail,
                subject: `Nuevo Pedido #${newOrder.id} - ${customerName}`,
                data: adminEmailData,
                status: 'pending'
              });
            }
          }

          // Obtener emails de admin para comprobante de transferencia
          const { data: transferSettings } = await supabase
            .from('notification_settings')
            .select('emails, enabled')
            .eq('notification_type', 'transfer_uploaded')
            .single();

          if (transferSettings?.enabled && transferSettings.emails?.length > 0) {
            for (const adminEmail of transferSettings.emails) {
              await supabase.from('email_queue').insert({
                type: 'admin_transfer_uploaded',
                recipient: adminEmail,
                subject: `Comprobante recibido - Pedido #${newOrder.id}`,
                data: adminEmailData,
                status: 'pending'
              });
            }
          }

          // Invocar process-email-queue para enviar los emails inmediatamente
          try {
            await supabase.functions.invoke('process-email-queue');
          } catch (emailError) {
            console.error('Error al procesar cola de emails:', emailError);
          }

          setSuccess('¡Comprobante de transferencia subido con éxito! Tu pedido está pendiente de verificación por nuestro equipo. Te notificaremos cuando sea confirmado.');
          setTimeout(() => {
            navigate(`/confirmation/${newOrder.id}`);
          }, 4000);
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Error al procesar el pedido');
    } finally {
      setLoading(false);
    }
  };

  // Limpiar carrito
  const clearCart = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: cart, error: cartError } = await supabase
      .from('cart')
      .select('id')
      .eq('user_id', user.id)
      .single();
    if (cartError) throw cartError;
    if (!cart) return;
    const { error: clearCartItemsError } = await supabase
      .from('cart_items')
      .delete()
      .eq('cart_id', cart.id);
    if (clearCartItemsError) throw clearCartItemsError;
    refreshCart();
  };

  // Guardar información del perfil
  const saveProfileInfo = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase
      .from('profiles')
      .update({
        email: formData.email,
        company: formData.companyName,
        name: formData.contactPerson,
        phone: formData.phone,
        nit: formData.nit,
        nit_type: formData.nitType,
        address: formData.address,
        address_details: formData.addressDetails,
        department: formData.department,
        municipio: formData.municipality,
        postal_code: formData.postalCode,
      })
      .eq('id', user.id);
    if (error) {
      console.error('Error saving profile info:', error);
      setError('Error al guardar la información del perfil');
    } else {
      setUserProfile((prev: any) => ({ ...prev, ...formData }));
      setSuccess('Información del perfil guardada');
    }
  };

  // Enviar correo de confirmación de pedido
  const sendConfirmationEmail = async (orderId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const order = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single();
    if (order.error) return;

    const orderItems = await supabase
      .from('order_items')
      .select('*')
      .eq('order_id', orderId);
    if (orderItems.error) return;

    const userProfile = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (userProfile.error) return;

    const emailContent = `
      <h2>¡Gracias por tu pedido!</h2>
      <p>Estimado ${userProfile.data.name || userProfile.data.company},</p>
      <p>Tu pedido con ID ${orderId} ha sido recibido y está siendo procesado.</p>
      <h3>Resumen del pedido:</h3>
      <ul>
        ${orderItems.data.map((item: any) => `
          <li>${item.product.name} (Cantidad: ${item.quantity}, Precio: Q${item.price.toFixed(2)})</li>
        `).join('')}
      </ul>
      <p>El total de tu pedido es de Q${order.data.total_amount.toFixed(2)}.</p>
      <p>Te notificaremos cuando tu pedido esté listo para ser enviado.</p>
      <p>Atentamente, Distribuidora del Sol Guatemala S.A.</p>
    `;

    await sendOrderConfirmationEmail(user.email || userProfile.data.email || '');
  };

  // --- FUNCIONES DE PRECIO MIGRADAS ---
  const getPriceInfo = (item: CartItem) => {
    const type = item.price_type || userPriceType || 'publico';
    if (!item.product.prices || item.product.prices.length === 0) {
      return { current: item.product.base_price, original: null } as { current: number; original: number | null };
    }
    const applicable = item.product.prices.filter(p => p.price_type === type);
    if (applicable.length === 0) {
      return { current: item.product.base_price, original: null };
    }
    const valid = applicable.filter(p => item.quantity >= p.min_quantity);
    // Seleccionar el rango con min_quantity más alto que aplique (ordenar descendente)
    const selected = valid.length > 0
      ? valid.sort((a, b) => b.min_quantity - a.min_quantity)[0]
      : applicable.sort((a, b) => a.min_quantity - b.min_quantity)[0];
    if (selected.offer_price != null) {
      return { current: selected.offer_price, original: selected.price };
    }
    return { current: selected.price, original: null };
  };

  const getProductPrice = (item: CartItem) => {
    return getPriceInfo(item).current;
  };

  const getCartTotal = () => {
    return cartItems.reduce((sum, item) => sum + (getProductPrice(item) * item.quantity), 0);
  };

  // El envío se cobra al momento de la entrega, no se suma al total
  const getShippingCost = () => {
    return 0;
  };

  // Función para obtener el total incluyendo envío
  const getTotalWithShipping = () => {
    return getCartTotal() + getShippingCost();
  };

  // Mostrar loading general
  if (loadingCart || loadingProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  // Si la orden ya está pagada o no es pendiente de pago
  if (orderId && (orderAlreadyPaid || (orderPaymentStatus && orderPaymentStatus !== 'pending'))) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          Esta orden ya fue pagada o no está pendiente de pago.
        </div>
      </div>
    );
  }

  return (
    <>
      <form className="h-screen w-screen overflow-hidden" onSubmit={handleSubmit}>
      <div className="bg-white h-full overflow-hidden">
        <div className="grid lg:grid-cols-2 h-full">
          {/* Left Column - Order Summary */}
          <div className="p-8 lg:p-12 border-r border-gray-200 flex flex-col h-full overflow-hidden bg-gray-50">
            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-200 rounded-lg transition-colors group"
                onClick={() => navigate(-1)}
              >
                <ChevronLeft className="w-5 h-5 text-gray-700 group-hover:text-blue-500 transition-colors" />
                <span className="font-medium text-gray-700 group-hover:text-blue-500 transition-colors">Volver</span>
              </button>
            </div>

            {/* Pay Amount */}
            <div className="mb-8">
              <h1 className="text-gray-600 text-sm mb-2">Resumen</h1>
              <div className="text-4xl font-semibold text-gray-900">Q{getTotalWithShipping().toFixed(2)}</div>
            </div>

            {/* Product Items */}
            <div className="space-y-4 mb-8 flex-1 overflow-y-auto min-h-0">
              {cartItems.map((item, idx) => {
                const info = getPriceInfo(item);
                const total = info.current * item.quantity;
                return (
                  <div className="flex items-center gap-3" key={item.id}>
                    <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center overflow-hidden">
                      {item.product.main_image ? (
                        <ProxyImage src={item.product.main_image} alt={item.product.name} className="w-8 h-8 object-cover rounded" />
                      ) : (
                        <div className="w-6 h-6 bg-blue-200 rounded"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">{item.product.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        Cantidad <span>{item.quantity}</span>
                      </div>
                    </div>
                    <div className="font-medium text-gray-900 text-sm">
                      {info.original ? (
                        <>
                          <span className="line-through text-xs text-gray-400 mr-1">Q{(info.original * item.quantity).toFixed(2)}</span>
                          <span className="text-red-600">Q{total.toFixed(2)}</span>
                          <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] rounded uppercase font-semibold">Oferta</span>
                        </>
                      ) : (
                        <>Q{total.toFixed(2)}</>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="space-y-2 border-t border-gray-200 pt-4">
                <div className="flex justify-between text-gray-600 text-sm">
                  <span>Subtotal</span>
                  <span>Q{getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600 text-sm">
                  <span>Envío</span>
                  <span className="text-gray-500 text-sm">Se cobra al momento de la entrega</span>
                </div>
                <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-200 pt-2">
                  <span>Total</span>
                  <span>Q{getTotalWithShipping().toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <img src="/assets/DISOLGUA.png" alt="Logo" className="h-16" />
              </div>
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div className="p-6 h-full overflow-y-auto" ref={formRef}>
            {/* Mensajes de error y éxito - Más visibles */}
            {error && (
              <div
                ref={errorRef}
                className="bg-red-50 border-2 border-red-400 text-red-700 px-4 py-4 rounded-xl mb-4 flex items-start gap-3 shadow-sm animate-pulse"
              >
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-red-800">Hay campos incompletos</div>
                  <div className="text-sm mt-1">{error}</div>
                </div>
              </div>
            )}
            {success && (
              <div className="bg-green-50 border-2 border-green-400 text-green-700 px-4 py-4 rounded-xl mb-4 flex items-start gap-3 shadow-sm">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-green-800">Procesando tu pedido</div>
                  <div className="text-sm mt-1">{success}</div>
                </div>
              </div>
            )}
            {/* Shipping Information */}
            <div className="mb-6">
              <h1 className="text-gray-900 text-3xl font-bold mb-2">Finalizar Compra</h1>
              <p className="text-gray-500 text-sm mb-4">Completa tus datos para enviarte tu pedido</p>
              <h2 className="text-base font-medium text-gray-900 mb-3">Dirección de envío</h2>
              {/* Saved Address */}
              {userProfile && userProfile.address && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-blue-600 font-medium mb-1 text-sm">Dirección guardada en tu perfil</div>
                  <div className="text-blue-600 text-xs mb-2">{userProfile.address}, {userProfile.municipio}, {userProfile.department}</div>
                  <button type="button" className="text-blue-600 text-xs hover:underline" onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      companyName: userProfile.company || '',
                      contactPerson: userProfile.name || '',
                      nit: userProfile.nit || '',
                      nitType: userProfile.nit_type || 'NIT',
                      address: userProfile.address || '',
                      addressDetails: userProfile.address_details || '',
                      department: userProfile.department || '',
                      municipality: userProfile.municipio || '',
                      postalCode: userProfile.postal_code || '',
                      phone: userProfile.phone || ''
                    }));
                    if (userProfile.department) fetchMunicipiosByDepartamento(userProfile.department, true);
                  }}>
                    Usar esta dirección
                  </button>
                </div>
              )}
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Correo electrónico <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-base transition-colors ${
                      fieldErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                    }`}
                    placeholder="tu@email.com"
                    disabled={loading}
                  />
                  {fieldErrors.email && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {fieldErrors.email}
                    </p>
                  )}
                </div>

                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tu nombre o empresa <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-base transition-colors ${
                        fieldErrors.contactPerson && !formData.companyName ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                      }`}
                      placeholder="Nombre de empresa"
                      disabled={loading}
                    />
                    <input
                      type="text"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-base transition-colors ${
                        fieldErrors.contactPerson && !formData.contactPerson ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                      }`}
                      placeholder="Tu nombre"
                      disabled={loading}
                    />
                  </div>
                  {fieldErrors.contactPerson && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {fieldErrors.contactPerson}
                    </p>
                  )}
                </div>

                {/* NIT */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Facturación
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="relative">
                      <select
                        name="nitType"
                        value={formData.nitType}
                        onChange={(e) => {
                          handleInputChange(e);
                          if (e.target.value === 'CF') {
                            setFieldErrors(prev => ({ ...prev, nit: undefined }));
                          }
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white text-base"
                        disabled={loading}
                      >
                        <option value="NIT">Con NIT</option>
                        <option value="CF">Sin NIT (CF)</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                    <input
                      type="text"
                      name="nit"
                      value={formData.nitType === "CF" ? "Consumidor Final" : formData.nit}
                      onChange={handleInputChange}
                      readOnly={formData.nitType === "CF"}
                      className={`col-span-2 w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-base transition-colors ${
                        formData.nitType === "CF" ? "bg-gray-100 text-gray-500 border-gray-200" :
                        fieldErrors.nit ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                      }`}
                      placeholder={formData.nitType === "CF" ? "Consumidor Final" : "Ej: 12345678-9"}
                      disabled={loading}
                    />
                  </div>
                  {fieldErrors.nit && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {fieldErrors.nit}
                    </p>
                  )}
                </div>

                {/* Dirección */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección de entrega <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-base transition-colors ${
                      fieldErrors.address ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                    }`}
                    placeholder="Calle, número, zona, colonia..."
                    disabled={loading}
                  />
                  {fieldErrors.address && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {fieldErrors.address}
                    </p>
                  )}
                </div>
                {/* Detalles adicionales (opcional) */}
                <input
                  type="text"
                  name="addressDetails"
                  value={formData.addressDetails}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base"
                  placeholder="Apartamento, casa, oficina... (opcional)"
                  disabled={loading}
                />

                {/* Departamento y Municipio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ubicación <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <select
                        name="department"
                        value={formData.department}
                        onChange={e => {
                          handleInputChange(e);
                          fetchMunicipiosByDepartamento(e.target.value);
                        }}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white text-base transition-colors ${
                          fieldErrors.department ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                        }`}
                        disabled={loading || loadingDepartamentos}
                      >
                        <option value="">Departamento</option>
                        {departamentos.map(dep => (
                          <option key={dep.id} value={dep.nombre}>{dep.nombre}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                    <div className="relative">
                      <select
                        name="municipality"
                        value={formData.municipality}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white text-base transition-colors ${
                          fieldErrors.municipality ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                        } ${!formData.department ? 'bg-gray-100 text-gray-400' : ''}`}
                        disabled={loading || loadingMunicipios || !formData.department}
                      >
                        <option value="">{loadingMunicipios ? 'Cargando...' : formData.department ? 'Municipio' : 'Primero elige departamento'}</option>
                        {municipiosFiltrados.map(mun => (
                          <option key={mun.id} value={mun.nombre}>{mun.nombre}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  {(fieldErrors.department || fieldErrors.municipality) && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {fieldErrors.department || fieldErrors.municipality}
                    </p>
                  )}
                </div>

                {/* Código Postal (oculto pero con valor por defecto) */}
                <input
                  type="hidden"
                  name="postalCode"
                  value={formData.postalCode || '01001'}
                />

                {/* Teléfono */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-base transition-colors ${
                      fieldErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                    }`}
                    placeholder="Ej: 5555 1234"
                    disabled={loading}
                  />
                  {fieldErrors.phone && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {fieldErrors.phone}
                    </p>
                  )}
                </div>

                {/* Guardar información */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="saveInfo"
                    checked={saveInfo}
                    onChange={e => setSaveInfo(e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={loading}
                  />
                  <label htmlFor="saveInfo" className="text-sm text-gray-700">
                    Guardar mis datos para futuras compras
                  </label>
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="mb-6">
              <h2 className="text-base font-medium text-gray-900 mb-3">Método de pago</h2>
              {/* Payment Method Selection */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('card')}
                  className={`p-2 border rounded-lg flex items-center gap-2 text-xs ${paymentMethod === "card" ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`}
                  disabled={loading}
                >
                  <div className="w-4 h-4 border-2 border-gray-400 rounded-full flex items-center justify-center">
                    {paymentMethod === "card" && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                  </div>
                  <span>💳 Tarjeta</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('bankTransfer')}
                  className={`p-2 border rounded-lg flex items-center gap-2 text-xs ${paymentMethod === "bankTransfer" ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`}
                  disabled={loading}
                >
                  <div className="w-4 h-4 border-2 border-gray-400 rounded-full flex items-center justify-center">
                    {paymentMethod === "bankTransfer" && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                  </div>
                  <span>🏦 Transferencia</span>
                </button>
              </div>
              {paymentMethod === "card" && (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-medium text-blue-800 mb-2">Pago con Tarjeta</h3>
                    <p className="text-sm text-blue-700 mb-3">
                      Al seleccionar esta opción y hacer clic en "Pagar", serás redirigido a nuestra pasarela de pago segura (Recurrente) para completar tu transacción con tarjeta.
                    </p>
                    <p className="text-sm text-blue-700">
                      Completarás el proceso en una página segura con certificado SSL. Una vez aprobado, volverás automáticamente a nuestro sitio.
                    </p>
                  </div>
                </div>
              )}
              {paymentMethod === "bankTransfer" && (
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-blue-800 text-sm">Instrucciones para pago por transferencia</h3>
                      <button
                        type="button"
                        onClick={() => setShowTransferModal(true)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-xs font-medium"
                      >
                        <Info size={14} />
                        Ver instrucciones detalladas
                      </button>
                    </div>
                    <p className="text-xs text-blue-700 mb-4">
                      Selecciona tu banco, realiza la transferencia y sube el comprobante. 
                      Haz clic en "Ver instrucciones detalladas" para ver el proceso paso a paso.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2 text-sm">Cuentas bancarias disponibles</h3>
                    <div className="space-y-2">
                      {[
                        { banco: "Banrural", cuenta: "3173027242" },
                        { banco: "Banco Industrial", cuenta: "1100021030" },
                        { banco: "BAM", cuenta: "3040076937" },
                      ].map((bank, index) => (
                        <label
                          key={index}
                          className="flex items-start gap-2 p-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="selectedBank"
                            value={bank.banco}
                            checked={formData.selectedBank === bank.banco}
                            onChange={handleInputChange}
                            className="mt-1"
                            disabled={loading}
                          />
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{bank.banco}</div>
                            <div className="text-xs text-gray-600">Cuenta: {bank.cuenta}</div>
                            <div className="text-xs text-gray-600">Distribuidora del sol Guatemala S.A.</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* Transfer Receipt Upload */}
                  <div>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                      <input
                        type="file"
                        id="bankTransferReceipt"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={loading}
                      />
                      <label htmlFor="bankTransferReceipt" className="cursor-pointer flex flex-col items-center gap-1">
                        {formData.bankTransferReceipt ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center bg-green-50 text-green-600 w-12 h-12 rounded-full mx-auto">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"></path></svg>
                            </div>
                            <p className="text-sm font-medium text-gray-900">{formData.bankTransferReceipt.name}</p>
                            <p className="text-xs text-gray-500">{(formData.bankTransferReceipt.size / 1024 / 1024).toFixed(2)} MB</p>
                            <button
                              type="button"
                              className="text-sm text-blue-600 hover:text-blue-700"
                              onClick={() => setFormData(prev => ({ ...prev, bankTransferReceipt: undefined }))}
                              disabled={loading}
                            >
                              Cambiar archivo
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center bg-gray-100 w-12 h-12 rounded-full mx-auto">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                            </div>
                            <p className="text-sm font-medium text-gray-900">Haz clic para subir</p>
                            <p className="text-xs text-gray-500">PNG, JPG o JPEG (máx. 5MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Pay Button - Grande y visible */}
            <button
              type="submit"
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg ${
                loading
                  ? 'bg-gray-400 text-white'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
              }`}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Procesando tu pedido...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {paymentMethod === 'card' ? '💳' : '🏦'} Pagar Q{getTotalWithShipping().toFixed(2)}
                </span>
              )}
            </button>

            {/* Mensaje de seguridad */}
            <p className="text-center text-xs text-gray-500 mt-3">
              🔒 Pago 100% seguro. Tus datos están protegidos.
            </p>
          </div>
        </div>
      </div>
    </form>

    {/* Modal de instrucciones para transferencia */}
    <TransferInstructionsModal
      isOpen={showTransferModal}
      onClose={() => setShowTransferModal(false)}
      selectedBank={formData.selectedBank}
    />
  </>
  );
}
